// Project Analytics (MOSYMPD-451) — API layer (live only).
//
// Every call POSTs to njm-corda-pcmp `${getApiBaseUrl(appUrl)}/projectAnalytics/<op>`
// which assembles the In-Process Purity and Product Release analytics from the
// ledger-replicated in_process_test_data / product_release_test_data. There is
// no bundled/mock data and no stage/test assumptions on the frontend. On a
// missing base URL or a failed call each function returns an empty envelope of
// the same shape so the screen renders an empty state instead of throwing.

import axios from 'axios';
import { appUrl } from '../../../../../../utils/config';
import { getApiBaseUrl } from '../../CommonMethods/CommonMethods';

const NS = 'projectAnalytics';
const post = (op, payload) => {
  const base = getApiBaseUrl(appUrl);
  if (!base) return Promise.reject(new Error('API base URL not available'));
  return axios.post(`${base}/${NS}/${op}`, payload || {});
};
const pickData = (res, fallback) => {
  const d = res && res.data && res.data.data;
  return res && res.data && res.data.status && d != null ? d : fallback;
};

// ===== DROPDOWN OPTIONS APIs =====

// [{ id, label }] — id is the value carried through stages/campaigns/analytics.
export const fetchAllProjectIdsAPI = async () => {
  try {
    const res = await post('projects', {});
    const data = pickData(res, []);
    return { status: true, data: Array.isArray(data) ? data : [] };
  } catch (err) {
    console.error('[ProjectAnalytics] projects failed:', err);
    return { status: false, data: [] };
  }
};

export const fetchStagesForProjectAPI = async (projectId) => {
  try {
    const res = await post('stages', { projectId });
    const data = pickData(res, []);
    return { status: true, data: Array.isArray(data) ? data : [] };
  } catch (err) {
    console.error('[ProjectAnalytics] stages failed:', err);
    return { status: false, data: [] };
  }
};

export const fetchCampaignsForProjectAPI = async (projectId, stage) => {
  try {
    const res = await post('campaigns', { projectId, stage });
    const data = pickData(res, []);
    return { status: true, data: Array.isArray(data) ? data : [] };
  } catch (err) {
    console.error('[ProjectAnalytics] campaigns failed:', err);
    return { status: false, data: [] };
  }
};

export const fetchBatchesAPI = async (campaignId, stage) => {
  try {
    const res = await post('batches', { campaignId, stage });
    const data = pickData(res, []);
    return { status: true, data: Array.isArray(data) ? data : [] };
  } catch (err) {
    console.error('[ProjectAnalytics] batches failed:', err);
    return { status: false, data: [] };
  }
};

// ===== ANALYTICS APIs =====

// In-Process Purity analytics → { context, batches, plots[] }
export const fetchInProcessAnalyticsAPI = async ({ projectId, campaignId, stage } = {}) => {
  try {
    const res = await post('inProcess', { projectId, campaignId, stage });
    const data = pickData(res, null);
    return { status: !!data, data };
  } catch (err) {
    console.error('[ProjectAnalytics] inProcess failed:', err);
    return { status: false, data: null };
  }
};

// Product Release analytics → { campaignId, subtitle, stages[] }
export const fetchProductReleaseAnalyticsAPI = async ({ projectId, campaignId, stage, batch } = {}) => {
  try {
    const res = await post('productRelease', { projectId, campaignId, stage, batch });
    const data = pickData(res, null);
    return { status: !!data, data };
  } catch (err) {
    console.error('[ProjectAnalytics] productRelease failed:', err);
    return { status: false, data: null };
  }
};
